import { ChevronDown, ChevronRight, Loader2, RefreshCw, Search, XCircle } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import citriniService from "../../../../services/citriniService";
import {
  CitriniFetchInfoItem,
  CitriniSkippedBasket,
  CitriniSkippedContract,
  CitriniSyncInfoItem,
  PagedResponse,
} from "../../../../types/citrini";
import { EmptyState, ErrorBanner, formatDateCell, LoadingSpinner, Pagination } from "../helpers";
import SyncStatusBadge from "../components/SyncStatusBadge";

interface SyncResultsTabProps {
  fetchInfoId: number;
  fetchInfo: CitriniFetchInfoItem;
  isDarkMode: boolean;
  onCountChange?: (count: number) => void;
}

const STALE_THRESHOLD_MS = 20 * 60 * 1000; // 20 minutes

const REASON_COLORS: Record<string, string> = {
  "Missing compositeFigi": "bg-yellow-100 text-yellow-800",
  "Missing compositeFigi, chinaC1Figi, and chinaC2Figi": "bg-yellow-100 text-yellow-800",
  "Primary exchange is VALUE": "bg-orange-100 text-orange-800",
  "Contract not found in instrument_data": "bg-red-100 text-red-800",
  "Zero weight": "bg-gray-100 text-gray-700",
  "Basket not synced in this run": "bg-yellow-100 text-yellow-800",
};

const SyncResultsTab: React.FC<SyncResultsTabProps> = ({
  fetchInfoId,
  fetchInfo,
  isDarkMode,
  onCountChange,
}) => {
  const [data, setData] = useState<PagedResponse<CitriniSyncInfoItem> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [nameMap, setNameMap] = useState<Record<string, string>>({});

  const load = useCallback(
    async (p: number) => {
      try {
        setLoading(true);
        setError(null);
        const res = await citriniService.listSyncInfo({
          citriniFetchInfoId: fetchInfoId,
          page: p,
          pageSize: 20,
          orderDir: "desc",
        });
        setData(res);
        setPage(p);
        onCountChange?.(res.totalItems);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load sync info");
      } finally {
        setLoading(false);
      }
    },
    [fetchInfoId]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  // Build citriniId -> name lookup from fetched baskets/portfolios
  useEffect(() => {
    let cancelled = false;
    async function loadNames() {
      try {
        const [baskets, portfolios] = await Promise.all([
          citriniService.listBasketsByFetchInfoId(fetchInfoId),
          citriniService.listPortfoliosByFetchInfoId(fetchInfoId),
        ]);
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const b of baskets) {
          map[b.citriniId] = b.name;
        }
        for (const p of portfolios) {
          map[p.citriniId] = p.name;
        }
        setNameMap(map);
      } catch {
        // Non-critical -- names just won't show
      }
    }
    loadNames();
    return () => { cancelled = true; };
  }, [fetchInfoId]);

  const toggleRow = useCallback((id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const [searchTerm, setSearchTerm] = useState("");

  const filteredItems = React.useMemo(() => {
    if (!data) return [];
    if (!searchTerm.trim()) return data.items;
    const term = searchTerm.toLowerCase();
    return data.items.filter(
      (item) =>
        item.citriniId.toLowerCase().includes(term) ||
        (nameMap[item.citriniId] || "").toLowerCase().includes(term)
    );
  }, [data, searchTerm, nameMap]);

  if (loading && !data) {
    return <LoadingSpinner isDarkMode={isDarkMode} />;
  }

  if (error && !data) {
    return <ErrorBanner message={error} onRetry={() => load(1)} isDarkMode={isDarkMode} />;
  }

  // Empty state with context
  if (data && data.items.length === 0) {
    if (!fetchInfo.finishedAtUtc) {
      return (
        <EmptyState
          message="Fetch still in progress"
          isDarkMode={isDarkMode}
          icon={<Loader2 className="w-6 h-6 text-yellow-500 animate-spin mb-2" />}
        />
      );
    }
    return <EmptyState message="Sync has not started yet" isDarkMode={isDarkMode} />;
  }

  if (!data) return null;

  const COLS = 13;

  const hasSkippedItems = (item: CitriniSyncInfoItem): boolean => {
    const d = item.data;
    if (!d) return false;
    return (
      (d.skippedContracts && d.skippedContracts.length > 0) ||
      (d.skippedBaskets && d.skippedBaskets.length > 0) ||
      false
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="relative">
          <Search className={`w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 ${isDarkMode ? "text-rp-grey/40" : "text-gray-400"}`} />
          <input
            type="text"
            placeholder="Search by ID or name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`text-xs rounded-lg border pl-8 pr-3 py-1.5 w-52 ${
              isDarkMode
                ? "bg-rp-surface border-rp-separator text-rp-grey/70 placeholder-rp-grey/40"
                : "bg-white border-gray-300 text-gray-700 placeholder-gray-400"
            }`}
          />
        </div>
        <button
          onClick={() => load(page)}
          disabled={loading}
          className={`px-3 py-1.5 text-xs font-medium rounded-lg border flex items-center ${
            isDarkMode
              ? "bg-rp-surface border-rp-separator text-rp-grey/70 hover:bg-rp-surface-raised disabled:opacity-50"
              : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          }`}
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr
              className={`text-left text-xs ${
                isDarkMode
                  ? "text-rp-grey/50 border-b border-rp-separator"
                  : "text-gray-500 border-b border-gray-200"
              }`}
            >
              <th className="pb-2 pr-2 font-medium w-8" />
              <th className="pb-2 pr-4 font-medium">ID</th>
              <th className="pb-2 pr-4 font-medium">Type</th>
              <th className="pb-2 pr-4 font-medium">Citrini ID</th>
              <th className="pb-2 pr-4 font-medium">Name</th>
              <th className="pb-2 pr-4 font-medium">Product ID</th>
              <th className="pb-2 pr-4 font-medium">Provider ID</th>
              <th className="pb-2 pr-4 font-medium">Status</th>
              <th className="pb-2 pr-4 font-medium">Published</th>
              <th className="pb-2 pr-4 font-medium">Resolved</th>
              <th className="pb-2 pr-4 font-medium">Skipped</th>
              <th className="pb-2 pr-4 font-medium">Started At</th>
              <th className="pb-2 font-medium">Finished At</th>
            </tr>
          </thead>
          <tbody className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
            {filteredItems.map((item) => {
              const isExpanded = expandedRows.has(item.id);
              const canExpand = hasSkippedItems(item);
              const skippedContracts = item.data?.skippedContracts || [];
              const skippedBaskets = item.data?.skippedBaskets || [];

              return (
                <React.Fragment key={item.id}>
                  <tr
                    onClick={() => canExpand && toggleRow(item.id)}
                    className={`${canExpand ? "cursor-pointer" : ""} ${
                      isDarkMode ? "hover:bg-rp-surface/50" : "hover:bg-gray-50"
                    }`}
                  >
                    <td className="py-2.5 pr-2">
                      {canExpand ? (
                        isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        )
                      ) : (
                        <span className="w-4 h-4 inline-block" />
                      )}
                    </td>
                    <td
                      className={`py-2.5 pr-4 font-mono text-xs ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                      }`}
                    >
                      {item.id}
                    </td>
                    <td className="py-2.5 pr-4">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                          item.productType === "BASKET"
                            ? "bg-purple-100 text-purple-800"
                            : "bg-indigo-100 text-indigo-800"
                        }`}
                      >
                        {item.productType}
                      </span>
                    </td>
                    <td
                      className={`py-2.5 pr-4 font-mono text-xs ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                      }`}
                    >
                      {item.citriniId}
                    </td>
                    <td
                      className={`py-2.5 pr-4 text-xs ${
                        isDarkMode ? "text-rp-grey" : "text-gray-800"
                      }`}
                    >
                      {nameMap[item.citriniId] || "-"}
                    </td>
                    <td
                      className={`py-2.5 pr-4 font-mono text-xs ${
                        isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                      }`}
                    >
                      {item.productId ?? "-"}
                    </td>
                    <td
                      className={`py-2.5 pr-4 font-mono text-xs ${
                        isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                      }`}
                    >
                      {item.providerId}
                    </td>
                    <td className="py-2.5 pr-4">
                      {item.data?.status ? (
                        <SyncStatusBadge status={item.data.status} />
                      ) : item.startedAtUtc && !item.finishedAtUtc ? (
                        Date.now() - new Date(item.startedAtUtc).getTime() > STALE_THRESHOLD_MS ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <XCircle className="w-3 h-3" />
                            failed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            syncing
                          </span>
                        )
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="py-2.5 pr-4">
                      {item.data?.published !== undefined ? (
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            item.data.published
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {item.data.published ? "Yes" : "No"}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td
                      className={`py-2.5 pr-4 text-center ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                      }`}
                    >
                      {item.data?.contractsResolved ?? "-"}
                      {item.data?.basketsResolved !== undefined && (
                        <span className={`text-xs ml-1 ${isDarkMode ? "text-rp-grey/40" : "text-gray-400"}`}>
                          ({item.data.basketsResolved}b)
                        </span>
                      )}
                    </td>
                    <td
                      className={`py-2.5 pr-4 text-center ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                      }`}
                    >
                      {item.data?.contractsSkipped ?? "-"}
                      {item.data?.basketsSkipped !== undefined && (
                        <span className={`text-xs ml-1 ${isDarkMode ? "text-rp-grey/40" : "text-gray-400"}`}>
                          ({item.data.basketsSkipped}b)
                        </span>
                      )}
                    </td>
                    <td
                      className={`py-2.5 pr-4 ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                      }`}
                    >
                      {formatDateCell(item.startedAtUtc)}
                    </td>
                    <td
                      className={`py-2.5 ${isDarkMode ? "text-rp-grey/70" : "text-gray-700"}`}
                    >
                      {formatDateCell(item.finishedAtUtc)}
                    </td>
                  </tr>

                  {isExpanded && canExpand && (
                    <tr>
                      <td colSpan={COLS} className="p-0">
                        <div
                          className={`px-8 py-3 space-y-4 ${
                            isDarkMode ? "bg-rp-surface/30" : "bg-gray-50"
                          }`}
                        >
                          {/* Skipped Contracts */}
                          {skippedContracts.length > 0 && (
                            <SkippedContractsTable
                              items={skippedContracts}
                              isDarkMode={isDarkMode}
                            />
                          )}

                          {/* Skipped Baskets */}
                          {skippedBaskets.length > 0 && (
                            <SkippedBasketsTable
                              items={skippedBaskets}
                              isDarkMode={isDarkMode}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination
        currentPage={page}
        totalPages={data.totalPages}
        totalItems={data.totalItems}
        onPageChange={load}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Inline sub-tables for skipped items
// ---------------------------------------------------------------------------

const SkippedContractsTable: React.FC<{
  items: CitriniSkippedContract[];
  isDarkMode: boolean;
}> = ({ items, isDarkMode }) => (
  <div>
    <h4
      className={`text-xs font-semibold mb-2 ${
        isDarkMode ? "text-rp-grey/50" : "text-gray-600"
      }`}
    >
      Skipped Contracts ({items.length})
    </h4>
    <table className="w-full text-xs">
      <thead>
        <tr
          className={`text-left ${
            isDarkMode
              ? "text-rp-grey/40 border-b border-rp-separator"
              : "text-gray-400 border-b border-gray-200"
          }`}
        >
          <th className="pb-1.5 pr-3 font-medium">Ticker / Ticker ID</th>
          <th className="pb-1.5 pr-3 font-medium">FIGI</th>
          <th className="pb-1.5 pr-3 font-medium text-right">Weight</th>
          <th className="pb-1.5 font-medium">Reason</th>
        </tr>
      </thead>
      <tbody
        className={`divide-y ${isDarkMode ? "divide-rp-separator/50" : "divide-gray-100"}`}
      >
        {items.map((c, i) => (
          <tr key={i}>
            <td
              className={`py-1.5 pr-3 font-mono ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
              }`}
            >
              {c.ticker || c.tickerId || "-"}
            </td>
            <td
              className={`py-1.5 pr-3 font-mono ${
                isDarkMode ? "text-rp-grey/50" : "text-gray-500"
              }`}
            >
              {`${c.compositeFigi ?? "null"},${c.chinaC1Figi ?? "null"},${c.chinaC2Figi ?? "null"}`}
            </td>
            <td
              className={`py-1.5 pr-3 text-right font-mono ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
              }`}
            >
              {c.weight}
            </td>
            <td className="py-1.5">
              <span
                className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                  REASON_COLORS[c.reason] || "bg-gray-100 text-gray-700"
                }`}
              >
                {c.reason}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SkippedBasketsTable: React.FC<{
  items: CitriniSkippedBasket[];
  isDarkMode: boolean;
}> = ({ items, isDarkMode }) => (
  <div>
    <h4
      className={`text-xs font-semibold mb-2 ${
        isDarkMode ? "text-rp-grey/50" : "text-gray-600"
      }`}
    >
      Skipped Baskets ({items.length})
    </h4>
    <table className="w-full text-xs">
      <thead>
        <tr
          className={`text-left ${
            isDarkMode
              ? "text-rp-grey/40 border-b border-rp-separator"
              : "text-gray-400 border-b border-gray-200"
          }`}
        >
          <th className="pb-1.5 pr-3 font-medium">Ticker ID</th>
          <th className="pb-1.5 pr-3 font-medium text-right">Weight</th>
          <th className="pb-1.5 font-medium">Reason</th>
        </tr>
      </thead>
      <tbody
        className={`divide-y ${isDarkMode ? "divide-rp-separator/50" : "divide-gray-100"}`}
      >
        {items.map((b, i) => (
          <tr key={i}>
            <td
              className={`py-1.5 pr-3 font-mono ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
              }`}
            >
              {b.tickerId}
            </td>
            <td
              className={`py-1.5 pr-3 text-right font-mono ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
              }`}
            >
              {b.weight}
            </td>
            <td className="py-1.5">
              <span
                className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                  REASON_COLORS[b.reason] || "bg-gray-100 text-gray-700"
                }`}
              >
                {b.reason}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default SyncResultsTab;
